import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/fr'
import 'dayjs/locale/pt'
import advancedFormat from 'dayjs/plugin/advancedFormat'

dayjs.extend(utc)
dayjs.extend(advancedFormat)

/**
 * Formats a UTC date string using a dayjs format pattern.
 * @param date - ISO date string
 * @param formatType - dayjs format string, e.g. `'DD/MM/YYYY'`
 * @param locale - BCP 47 locale tag (`'en'`, `'fr'`, `'pt'`)
 */
export const formattedDate = (date: string, formatType: string, locale = 'en') => {
	return dayjs.utc(date).locale(locale).format(formatType)
}

/**
 * Returns a date formatted as `'MMMM, YYYY'` (e.g. `'January, 2024'`).
 * @param date - ISO date string
 * @param locale - BCP 47 locale tag
 */
export const getMonthYear = (date: string, locale = 'en') => {
	return dayjs(date).locale(locale).format('MMMM, YYYY')
}

/**
 * Returns a date formatted as `'D MMM YYYY'` (e.g. `'5 Jan 2024'`).
 * @param date - ISO date string
 * @param locale - BCP 47 locale tag
 */
export const getAbbrMonth = (date: string, locale = 'en') => {
	return dayjs(date).locale(locale).format('D MMM YYYY')
}

/** Returns the current year as a 4-digit string (e.g. `'2024'`). */
export const getCurrentYear = () => {
	return dayjs().format('YYYY')
}

/**
 * Returns a date formatted as `'MMMM DD, YYYY'` (e.g. `'January 05, 2024'`).
 * @param date - ISO date string
 * @param locale - BCP 47 locale tag
 */
export const getMonthDayYear = (date: string, locale = 'en') => {
	return dayjs(date).locale(locale).format('MMMM DD, YYYY')
}

/**
 * Returns a compact date formatted as `'MMM, DD - YYYY'` intended for mobile displays.
 * @param date - ISO date string
 * @param locale - BCP 47 locale tag
 */
export const getMonthDayYearMobile = (date: string, locale = 'en') => {
	return dayjs(date).locale(locale).format('MMM, DD - YYYY')
}

/**
 * Returns a date formatted as `'MMMM Do, YYYY'` (EN) or `'D MMMM YYYY'` (other locales).
 * @param date - ISO date string
 * @param locale - BCP 47 locale tag
 */
export const getFullDate = (date: string, locale = 'en') => {
	if (locale === 'en') {
		return dayjs(date).locale(locale).format('MMMM Do, YYYY')
	}

	return dayjs(date).locale(locale).format('D MMMM YYYY')
}

/**
 * Returns a UTC date/time string (e.g. `'Jan 5, 2024 • 3:00 PM'` for EN).
 * @param date - ISO date-time string
 * @param locale - BCP 47 locale tag
 */
export const getDateWithTime = (date: string, locale = 'en') => {
	if (locale === 'en') {
		return dayjs.utc(date).locale(locale).format('MMM D, YYYY • h:mm A')
	}
	return dayjs.utc(date).locale(locale).format('D MMM YYYY • HH:mm')
}
